/**
 * URL sanitization utilities for preventing XSS attacks via dangerous URL protocols.
 *
 * Uses an allowlist approach: only permits known-safe protocols (http, https, mailto, tel, ftp)
 * and blocks all others including javascript:, data:, vbscript:, file:, blob:.
 */

// Mirrors backend JSoup safelist used in HubSpot backend services
// See: FormsBase/SafeLists.java, FeedbackCore/RichTextFieldParser.java
const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:', 'ftp:'];

// eslint-disable-next-line no-script-url
const NOOP_HREF = 'javascript:void(0)';

// Matches protocol at start of URL (e.g., "https:" from "https://example.com")
const PROTOCOL_REGEX = /^([^:]+):/;

// Matches whitespace and control characters (ASCII 0x00-0x1F) used to bypass
// protocol detection (e.g., "java\tscript:" or "java\nscript:")
// eslint-disable-next-line no-control-regex
const IGNORED_PROTOCOL_CHARS = /[\s\x00-\x1f]/g;

/**
 * Check if a URL has a safe protocol using allowlist approach.
 * Blocks javascript:, data:, vbscript:, file:, blob: and any unknown protocols.
 * Only allows safe protocols: http(s), mailto, tel, ftp
 *
 * @param href - The URL to check
 * @returns true if the URL protocol is safe or if it's a relative URL
 */
export function isSafeUrlProtocol(href) {
  if (!href) {
    return true; // Empty/null/undefined URLs are safe
  }

  // Relative URLs (no protocol) are safe
  const protocolMatch = href.match(PROTOCOL_REGEX);
  if (!protocolMatch) {
    return true;
  }

  // javascript:void(0) is explicitly allowed as no-op
  if (href === NOOP_HREF) {
    return true;
  }

  // Extract and normalize protocol
  const protocol = protocolMatch[0].replace(IGNORED_PROTOCOL_CHARS, '').toLowerCase();

  // Check against allowlist
  return SAFE_URL_PROTOCOLS.includes(protocol);
}

/**
 * Check if a URL contains javascript: protocol (case-insensitive, ignoring whitespace/control chars).
 * This is the original blocklist-based check used for backwards compatibility.
 *
 * @param href - The URL to check
 * @returns true if the URL is a javascript: URL
 */
export function isJavascriptUrl(href) {
  if (!href) {
    return false;
  }
  const protocolMatch = href.match(PROTOCOL_REGEX);
  if (!protocolMatch || href === NOOP_HREF) return false;
  const normalizedProtocol = protocolMatch[0].replace(IGNORED_PROTOCOL_CHARS, '').toLowerCase();
  // eslint-disable-next-line no-script-url
  return normalizedProtocol === 'javascript:';
}

/**
 * Sanitize URL to prevent XSS attacks.
 * Uses both blocklist (javascript:) and allowlist (safe protocols) approach.
 *
 * @param href - The URL to sanitize
 * @returns Sanitized URL, or javascript:void(0) if unsafe
 */
export function sanitizeUrl(href) {
  if (!href) {
    return href; // Preserve null/undefined/empty string
  }
  return isJavascriptUrl(href) || !isSafeUrlProtocol(href) ? NOOP_HREF : href;
}

/**
 * The no-op URL used to replace unsafe URLs.
 * Exported for use in components that need to check if a URL was sanitized.
 */
export { NOOP_HREF };